import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  EmployeeCheckSource,
  EmployeeCheckSourceUpdateDto,
} from '@qaroni-app/employee/types/employee-check-source';
import {
  EmployeeCheckSourceApiObject,
  EmployeeCheckSourceApiResponse,
} from '@qaroni-app/employee/types/employee-check-source-response';
import { EmployeeConfigurationCheckCache } from '@qaroni-app/employee/types/employee-configuration-check-cache';
import { QaroniIntermediate } from '@qaroni-core/types/qaroni-intermediate/qaroni-intermediate';
import { ReplaySubject, Subject } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';
import { EmployeeSnackbarsService } from '../employee-snackbars.service';
import { EmployeeCheckSourceHttpService } from './employee-check-source-http.service';

@Injectable({
  providedIn: 'root',
})
export class EmployeeCheckSourceService extends QaroniIntermediate {
  private http = inject(EmployeeCheckSourceHttpService);
  private snackbarService = inject(EmployeeSnackbarsService);

  private checksSourceSubject = new ReplaySubject<EmployeeCheckSource[]>();
  private checkSourceSubject = new Subject<EmployeeCheckSource | null>();
  private checkSourceCache = new EmployeeConfigurationCheckCache();

  public getCheckSourceSubject$() {
    return this.checkSourceSubject.asObservable().pipe(shareReplay(1));
  }

  public getChecksSourceSubject$() {
    return this.checksSourceSubject.asObservable().pipe(shareReplay(1));
  }

  public createCheckConfiguration(
    companyID: number,
    employeeID: number,
    checkSource: EmployeeCheckSource
  ) {
    this.http
      .createChecksConfiguration$(companyID, employeeID, checkSource)
      .subscribe({
        next: this.nextCreateCheckConfiguration,
        error: this.errorCreateCheckConfiguration,
      });
  }

  private nextCreateCheckConfiguration = (
    response: EmployeeCheckSourceApiObject
  ) => {
    if (!response) {
      this.checkSourceSubject.next(null);
    }
    this.checkSourceSubject.next(response.result[0]);
  };

  private errorCreateCheckConfiguration = () => {
    this.checkSourceSubject.next(null);
  };

  public updateCheckConfiguration(
    companyID: number,
    employeeID: number,
    checkSource: EmployeeCheckSourceUpdateDto
  ) {
    this.http
      .updateChecksConfiguration$(companyID, employeeID, checkSource)
      .subscribe({
        next: this.nextUpdateCheckConfiguration,
        error: this.errorUpdateCheckConfiguration,
      });
  }

  private nextUpdateCheckConfiguration = (
    response: EmployeeCheckSourceApiObject
  ) => {
    if (!response) {
      this.checkSourceSubject.next(null);
    }
    this.checkSourceSubject.next(response.result[0]);
    this.snackbarService.successChannelUpdate();
  };

  private errorUpdateCheckConfiguration = (error: HttpErrorResponse) => {
    this.checkSourceSubject.next(null);
    if (this.commons.errorsHttp.isControlledError(error, 'E0091')) {
      this.snackbarService.failureUpdateNfc();
    } else {
      this.snackbarService.failureChannelUpdate();
    }
  };

  public updateNfcRegistration(
    companyID: number,
    employeeID: number,
    enableNfcRegistration: boolean
  ) {
    this.http
      .updateChecksConfiguration$(companyID, employeeID, {
        enableNfcRegistration,
      })
      .subscribe({
        next: this.nextUpdateNfcRegistration,
        error: this.errorUpdateNfcRegistration,
      });
  }

  private nextUpdateNfcRegistration = (
    response: EmployeeCheckSourceApiObject
  ) => {
    const checkSource = response?.result?.[0];
    if (!checkSource) {
      this.checkSourceSubject.next(null);
      this.snackbarService.failureNfcRegistrationUpdate();
      return;
    }

    this.checkSourceSubject.next(checkSource);
    if (checkSource.enableNfcRegistration) {
      this.snackbarService.successNfcRegistrationEnabled();
    } else {
      this.snackbarService.successNfcRegistrationDisabled();
    }
  };

  private errorUpdateNfcRegistration = () => {
    this.checkSourceSubject.next(null);
    this.snackbarService.failureNfcRegistrationUpdate();
  };

  public deleteCheckConfiguration(companyID: number, employeeID: number) {
    this.http.deleteChecksConfiguration$(companyID, employeeID).subscribe({
      next: this.nextDeleteCheckConfiguration,
      error: this.errorDeleteCheckConfiguration,
    });
  }

  private nextDeleteCheckConfiguration = (
    response: EmployeeCheckSourceApiResponse
  ) => {
    if (this.status204(response)) {
      this.messageSubject.next('delete ok');
    }
    this.messageSubject.next('');
  };

  private errorDeleteCheckConfiguration = () => {
    this.messageSubject.next('');
  };

  public getCompanyCheckConfiguration(companyID: number, employeeID: number) {
    this.http.getCompanyChecksConfiguration$(companyID, employeeID).subscribe({
      next: this.nextGetCheckConfiguration,
      error: this.errorGetCheckConfiguration,
    });
  }

  private nextGetCheckConfiguration = (
    response: EmployeeCheckSourceApiObject
  ) => {
    if (!response || response.result.length === 0) {
      this.checksSourceSubject.next([]);
      return;
    }

    this.checksSourceSubject.next(response.result);
  };

  private errorGetCheckConfiguration = () => {
    this.checksSourceSubject.next([]);
  };

  public getEmployeeCheckConfiguration(employeeID: number) {
    if (this.checkSourceCache.canReturn(employeeID)) {
      this.checksSourceSubject.next([this.checkSourceCache.get()]);
    } else if (this.checkSourceCache.canApi(employeeID)) {
      this.checkSourceCache.startToFly();
      this.http
        .getEmployeeChecksConfiguration$(employeeID)
        .pipe(finalize(() => this.checkSourceCache.endFlying()))
        .subscribe({
          next: this.nextGetEmployeeCheckConfiguration,
          error: this.errorGetEmployeeCheckConfiguration,
        });
    }
  }

  private nextGetEmployeeCheckConfiguration = (
    response: EmployeeCheckSourceApiObject
  ) => {
    if (!response) {
      this.checksSourceSubject.next([]);
      return;
    }
    this.checkSourceCache.set(response.result[0]);
    this.checksSourceSubject.next([this.checkSourceCache.get()]);
  };

  private errorGetEmployeeCheckConfiguration = () => {
    this.checksSourceSubject.next([]);
  };
}
